import type { LineSeriesOption, YAXisComponentOption } from 'echarts';

import type { TimeSeries } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';

import { PLOT_GRID_INDEX } from '../config/chartLayout';
import { SLIDER_PREVIEW_SERIES_ID } from '../config/componentIds';
import { getOverviewLineSeriesData } from '../plot/level-of-detail/lineSeriesDataCache';

export type SliderPreviewOption = {
  yAxis: YAXisComponentOption;
  series: LineSeriesOption;
};

type BuildSliderPreviewOptionParams = {
  /** The series whose shape the slider should preview; undefined leaves the preview empty. */
  previewSeries: TimeSeries | undefined;
  yAxisIndex: number;
  /** Same drawing budget as the visible lines; the preview is drawn (invisibly) on every navigation step too. */
  bucketCount: number;
};

/**
 * ECharts binds its slider shadow to a series once, when the chart is first created, and never
 * re-picks it — reordering series later has no effect, on a merge or a replaceMerge alike. So the
 * preview gets a series of its own that is always present at the same position: swapping *its
 * data* is what changes the shadow, and that works on every update.
 *
 * The series is invisible in the plot and carries the whole domain on purpose: the slider represents
 * the whole domain, so its preview must not shrink as the viewport zooms in. It gets the thinned
 * whole-series overview, not the raw points: although transparent, it is still a line series that
 * ECharts re-processes on every navigation step, so raw data here would cost as much as a visible line.
 */
export function buildSliderPreviewOption({
  previewSeries,
  yAxisIndex,
  bucketCount,
}: BuildSliderPreviewOptionParams): SliderPreviewOption {
  return {
    yAxis: {
      type: 'value',
      gridIndex: PLOT_GRID_INDEX,
      show: false,
      axisPointer: { show: false },
    },
    series: {
      id: SLIDER_PREVIEW_SERIES_ID,
      type: 'line',
      yAxisIndex,
      silent: true,
      showSymbol: false,
      // Drawn in the plot but fully transparent: only its data matters, for the shadow.
      lineStyle: { opacity: 0 },
      itemStyle: { opacity: 0 },
      emphasis: { disabled: true },
      data: previewSeries ? getOverviewLineSeriesData(previewSeries, bucketCount).data : [],
    },
  };
}
